import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CheckCircle2, Loader2, Mail, Plus, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { useEffectiveMe } from '@/lib/effectiveMe';

const GROUP_TENANT_ID = '3b1c4198-a83a-49bb-a06e-03ada7a134a5';
const tenantLabel = (tenant) => (
  tenant?.display_name
  || (tenant?.id === GROUP_TENANT_ID ? 'Gruppo RHIO · RIBES · SUSHINBOX' : tenant?.name)
);

function isCeoTenantContext(me) {
  return Boolean(me?.is_global && me?.active_tenant_id);
}

export default function AdminUsers() {
  const me = useEffectiveMe();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTenantLabel, setActiveTenantLabel] = useState('');
  const ceoTenantContext = isCeoTenantContext(me);
  const globalNeedsTenant = Boolean(me?.is_global && !me?.active_tenant_id);
  const titolareAccounts = useMemo(
    () => users.filter((user) => (user.roles || []).includes('TITOLARE')),
    [users],
  );

  const load = useCallback(async () => {
    if (!ceoTenantContext) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const visibleUsers = await apiFetch('/api/security/users?role_code=TITOLARE');
      setUsers(visibleUsers || []);
    } catch (e) {
      toast.error(e.message || 'Errore caricamento accesso Titolare');
    } finally {
      setLoading(false);
    }
  }, [ceoTenantContext]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!ceoTenantContext) {
      setActiveTenantLabel('');
      return;
    }
    apiFetch('/api/ceo/tenants')
      .then((tenants) => {
        const active = (tenants || []).find((tenant) => tenant.id === me.active_tenant_id);
        setActiveTenantLabel(active ? tenantLabel(active) : 'Tenant selezionato');
      })
      .catch(() => setActiveTenantLabel('Tenant selezionato'));
  }, [ceoTenantContext, me?.active_tenant_id]);

  if (globalNeedsTenant) {
    return (
      <div className="space-y-4" data-testid="admin-users-tenant-required">
        <Link to="/foundation#tenant-clienti" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900">
          <ArrowLeft className="h-3 w-3" /> Dashboard CEO
        </Link>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h1 className="font-display text-2xl font-semibold text-brand-navy">Seleziona un tenant</h1>
          <p className="mt-2 text-sm text-stone-600">La gestione dell&apos;accesso Titolare è disponibile soltanto nel contesto di un tenant.</p>
        </div>
      </div>
    );
  }

  if (!ceoTenantContext) {
    return (
      <div className="space-y-4" data-testid="admin-users-personale-redirect">
        <div>
          <h1 className="font-display text-2xl font-semibold text-brand-navy">Accessi delle persone</h1>
          <p className="mt-2 max-w-xl text-sm text-stone-600">
            Gli accessi di dipendenti e responsabili si creano e si gestiscono dalla scheda della persona nel modulo Personale.
          </p>
        </div>
        <Button asChild className="w-fit gap-2 bg-brand-green text-white hover:bg-brand-green-hover">
          <Link to="/personale">Apri Personale</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/foundation" className="mb-2 inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900" data-testid="back-to-dashboard">
            <ArrowLeft className="h-3 w-3" /> Dashboard
          </Link>
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-brand-navy md:text-3xl">
            <ShieldCheck className="h-6 w-6 text-brand-green" /> Gestisci accessi Titolari
          </h1>
          <p className="mt-1 text-xs text-stone-500">Questa console gestisce esclusivamente l&apos;accesso Titolare del tenant.</p>
          <p className="mt-2 text-sm font-semibold text-brand-navy" data-testid="admin-users-active-tenant">
            Tenant: {activeTenantLabel || 'Tenant selezionato'}
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-brand-green">
          <ShieldCheck className="h-3 w-3" /> Isolamento server-side
        </span>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-stone-400">Accesso Titolare</div>
          <h2 className="mt-1 text-lg font-semibold text-brand-navy">Titolari del tenant</h2>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-stone-500" data-testid="titolare-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifica accesso esistente…
          </div>
        )}

        {!loading && titolareAccounts.length > 0 && (
          <div className="space-y-4" data-testid="existing-titolare-status">
            <div className="space-y-2">
              {titolareAccounts.map((account) => (
                <div key={account.id || account.email} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50 p-4" data-testid="titolare-account-row">
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-brand-navy">
                      <CheckCircle2 className="h-4 w-4 text-brand-green" />
                      {account.full_name || account.email}
                    </div>
                    <div className="mt-1 break-all text-sm text-stone-600">{account.email}</div>
                    <div className="mt-1 text-xs text-stone-500">
                      Stato: {account.active ? 'Attivo' : 'Archiviato'} · account tenant-wide · nessuna Persona collegata
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-stone-500">Puoi aggiungere altri Titolari. La gestione delle persone e dei relativi accessi avviene da Personale.</p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={load} className="gap-1" data-testid="refresh-titolare-status">
                  <RefreshCw className="h-3.5 w-3.5" /> Aggiorna stato
                </Button>
                <Button type="button" onClick={() => setShowCreate(true)} className="gap-2 bg-brand-green text-white hover:bg-brand-green-hover" data-testid="btn-create-titolare">
                  <Plus className="h-4 w-4" /> Aggiungi Titolare
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && titolareAccounts.length === 0 && (
          <div data-testid="titolare-create-state">
            <p className="mb-4 text-sm text-stone-600">Nessun accesso Titolare è ancora presente per questo tenant.</p>
            <Button onClick={() => setShowCreate(true)} className="gap-2 bg-brand-green text-white hover:bg-brand-green-hover" data-testid="btn-create-titolare">
              <Plus className="h-4 w-4" /> Crea accesso Titolare
            </Button>
          </div>
        )}
      </div>

      {showCreate && (
        <TitolareCreateModal
          tenantLabel={activeTenantLabel}
          onClose={() => setShowCreate(false)}
          onDone={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function TitolareCreateModal({ tenantLabel: currentTenantLabel, onClose, onDone }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const emailValue = email.trim();
    const firstNameValue = firstName.trim();
    const lastNameValue = lastName.trim();
    if (!firstNameValue || !lastNameValue || !emailValue) return;
    setBusy(true);
    try {
      await apiFetch('/api/security/access', {
        method: 'POST',
        body: JSON.stringify({
          first_name: firstNameValue,
          last_name: lastNameValue,
          email: emailValue,
          role_code: 'TITOLARE',
        }),
      });
      toast.success('Accesso Titolare creato');
      onDone();
    } catch (e) {
      toast.error(e.message || 'Impossibile creare l’accesso Titolare');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="titolare-create-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-app-border bg-app-surface p-5 sm:p-6" onClick={(event) => event.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Gestisci accesso Titolare</div>
            <h2 className="mt-1 text-xl font-semibold text-brand-navy">Crea accesso Titolare</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="btn-close-titolare-modal" aria-label="Chiudi" className="rounded-full p-2 text-stone-400 hover:bg-stone-50 hover:text-brand-navy">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="standalone-titolare-form">
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs text-stone-700">
            Account standalone, tenant-wide e non collegato a una Persona.
          </div>
          <div>
            <Label>Tenant</Label>
            <div className="mt-1.5 min-h-11 rounded-md border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm font-medium text-brand-navy" data-testid="titolare-tenant-context">
              {currentTenantLabel || 'Tenant selezionato'}
            </div>
            <p className="mt-1 text-[11px] text-stone-500">Il tenant è determinato dal contesto attivo e non è modificabile.</p>
          </div>
          <div>
            <Label htmlFor="titolare-first-name">Nome *</Label>
            <Input id="titolare-first-name" required value={firstName} onChange={(event) => setFirstName(event.target.value)} data-testid="titolare-first-name" />
          </div>
          <div>
            <Label htmlFor="titolare-last-name">Cognome *</Label>
            <Input id="titolare-last-name" required value={lastName} onChange={(event) => setLastName(event.target.value)} data-testid="titolare-last-name" />
          </div>
          <div>
            <Label htmlFor="titolare-email">Email Titolare *</Label>
            <Input id="titolare-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} data-testid="titolare-email" placeholder="titolare@azienda.it" />
          </div>
          <Button type="submit" disabled={busy || !firstName.trim() || !lastName.trim() || !email.trim()} className="w-full gap-2 bg-brand-green text-white hover:bg-brand-green-hover" data-testid="titolare-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {busy ? 'Creazione…' : 'Crea accesso Titolare'}
          </Button>
        </form>
      </div>
    </div>
  );
}